import type { FC } from "react";
import { useForm } from "@tanstack/react-form";

import { usePatchPatient } from "@/api/patients";
import Edit from "@/assets/icons/edit";
import { FieldInput, FieldRadio, FieldSelect } from "@/components/form";
import DialogCustom from "@/components/modals/dialog-custom";
import RiskModalFooter from "@/components/modals/profil-risk/risk-modal-footer";
import type { RiskUpdateModalProps } from "@/components/modals/profil-risk/risk-update-modal";
import { Separator } from "@/components/ui/separator";
import {
  patchGeneticDefaultValues,
  patchGeneticSchema,
} from "@/schemas/patient";
import { noYesRadioOptions, resetForm } from "@/utils/form";
import { stateOptions } from "@/utils/patient";

const GeneticModal: FC<RiskUpdateModalProps> = ({
  riskModalOpen,
  setRiskModalOpen,
  patient,
}) => {
  const { mutate } = usePatchPatient(patient.id);

  const { geneticState, geneticTendency, geneticComment } = patient;

  const { Field, handleSubmit } = useForm({
    defaultValues: {
      geneticState,
      geneticTendency,
      geneticComment:
        geneticComment ?? patchGeneticDefaultValues.geneticComment,
    },
    onSubmit: async ({ value, formApi }) => {
      const values = patchGeneticSchema.parse(value);
      mutate(value, {
        onSuccess: () => {
          setRiskModalOpen("none");
          resetForm(values, formApi);
        },
      });
    },
  });

  return (
    <DialogCustom
      title="Modifier les informations"
      startIcon={
        <div className="flex p-3 w-12 h-12 justify-center items-center border border-gray-200 rounded-[10px]">
          <Edit className="w-6 h-6" color="black" />
        </div>
      }
      description={
        <FieldSelect
          Field={Field}
          schema={patchGeneticSchema}
          name="geneticState"
          options={stateOptions}
          validators={{
            onChange: patchGeneticSchema.shape.geneticState,
          }}
          risk={"geneticTendency"}
        />
      }
      open={riskModalOpen !== "none"}
      onOpenChange={() => setRiskModalOpen("none")}
      className="flex flex-col gap-4"
    >
      <Separator />
      <form onSubmit={handleSubmit} className="flex flex-col gap-4 w-full px-6">
        <FieldRadio
          Field={Field}
          schema={patchGeneticSchema}
          name="geneticTendency"
          label="Patient prédisposé génétiquement"
          options={noYesRadioOptions}
          orientation="horizontal"
          type="boolean"
          validators={{
            onChange: patchGeneticSchema.shape.geneticTendency,
          }}
        />
        <FieldInput
          Field={Field}
          schema={patchGeneticSchema}
          name="geneticComment"
          placeholder="Ajouter un commentaire"
          label="Commentaire"
          validators={{
            onChange: patchGeneticSchema.shape.geneticComment,
          }}
        />
        <input type="submit" className="hidden" />
      </form>
      <Separator />
      <RiskModalFooter
        setRiskModalOpen={setRiskModalOpen}
        handleSubmit={handleSubmit}
      />
    </DialogCustom>
  );
};

export default GeneticModal;
